import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { apiClient, unwrap } from '../services/apiClient';
import { RenewalBanner } from '../components/layout/RenewalBanner';

const clientPortalNav = [
  { to: '/dashboard', label: 'Home', icon: 'home' },
  { to: '/tickets', label: 'Support Ticket', icon: 'confirmation_number' },
  { to: '/tickets/new', label: 'New Ticket', icon: 'add_circle' },
  { to: '/services', label: 'Addons Services', icon: 'design_services' },
  { to: '/fund-requests', label: 'Fund Request', icon: 'request_quote' },
  { to: '/ledger', label: 'User Ledger', icon: 'account_balance_wallet' },
  { to: '/invoices', label: 'Invoice List', icon: 'receipt_long' },
  { to: '/kyc', label: 'KYC', icon: 'verified_user' },
  { to: '/kb', label: 'Knowledge Base', icon: 'menu_book' },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

function ClientPortalShell({
  user,
  logout,
  navigate,
  toggleTheme,
  location,
  profileOpen,
  setProfileOpen,
}) {
  const walletBalance = user?.walletBalance ?? user?.balance ?? 0;

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,var(--color-primary-container),transparent_34rem),var(--color-background)] text-on-background transition-colors duration-300">
      <header className="sticky top-0 z-50 border-b border-app bg-surface/95 shadow-sm shadow-inverse-surface/5 backdrop-blur-xl">
        <div className="flex min-h-[64px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-10">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img src="/logo.png" alt="Company logo" className="h-12 w-auto object-contain" />
          </Link>

          <div className="flex min-w-0 items-center justify-end gap-2 sm:gap-3">
            <div className="hidden rounded-full border border-app bg-surface-muted px-3 py-2 text-sm font-bold text-on-surface sm:block">
              Balance: {formatCurrency(walletBalance)}
            </div>

            <button
              onClick={toggleTheme}
              className="app-button-ghost rounded-full p-2"
              title="Toggle theme"
            >
              <span className="material-symbols-outlined text-[20px]">settings_brightness</span>
            </button>

            <div className="relative">
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                title="User account"
                className="relative z-20 flex items-center gap-2 rounded-full border border-app bg-surface-muted px-2 py-1.5 text-sm font-bold text-on-surface transition hover:border-primary/40 hover:bg-primary-container/40"
              >
                <span className="hidden max-w-[180px] truncate sm:inline">Welcome, {user?.name || 'User'}</span>
                <span className="grid h-8 w-8 place-items-center rounded-full bg-primary-container text-primary">
                  <span className="material-symbols-outlined text-[19px]">person</span>
                </span>
                <span className="material-symbols-outlined text-[18px]">expand_more</span>
              </button>

              {profileOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setProfileOpen(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.98 }}
                    className="app-dropdown absolute right-0 z-20 mt-2 w-64 overflow-hidden"
                  >
                    <div className="border-b border-app px-4 py-3">
                      <p className="truncate text-sm font-bold text-on-surface">{user?.name}</p>
                      <p className="truncate text-xs text-muted">{user?.email}</p>
                    </div>
                    <Link
                      to="/settings"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-on-surface hover:bg-surface-container-low"
                      onClick={() => setProfileOpen(false)}
                    >
                      <span className="material-symbols-outlined text-[18px]">manage_accounts</span>
                      Profile / Settings
                    </Link>
                    <button
                      onClick={async () => {
                        setProfileOpen(false);
                        await logout();
                        navigate('/login');
                      }}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-semibold text-error hover:bg-error-container/20"
                    >
                      <span className="material-symbols-outlined text-[18px]">logout</span>
                      Sign Out
                    </button>
                  </motion.div>
                </>
              )}
            </div>
          </div>
        </div>

        <nav className="border-t border-app bg-primary text-on-primary shadow-sm shadow-primary/20">
          <div className="flex gap-1 overflow-x-auto px-3 py-1.5 sm:px-6 lg:px-10">
            {clientPortalNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/dashboard'}
                className={({ isActive }) =>
                  `group flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-sm font-bold transition sm:px-4 ${
                    isActive
                      ? 'bg-white/18 text-white shadow-inner'
                      : 'text-white/88 hover:bg-white/12 hover:text-white'
                  }`
                }
              >
                <span className="material-symbols-outlined text-[18px]">{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <main className="mx-auto w-full max-w-[1840px] px-4 py-6 sm:px-6 lg:px-10">
        <Outlet />
      </main>
    </div>
  );
}

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const { toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteResults, setPaletteResults] = useState([]);
  const [profileOpen, setProfileOpen] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      if (paletteOpen && paletteResults[0]?.path) {
        setPaletteOpen(false);
        navigate(paletteResults[0].path);
      } else {
        setPaletteOpen(false);
        navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (!paletteOpen || searchQuery.trim().length < 1) {
      setPaletteResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const response = await apiClient.get('/search/command-palette', { params: { q: searchQuery.trim() } });
        if (!cancelled) setPaletteResults(unwrap(response));
      } catch {
        if (!cancelled) setPaletteResults([]);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [paletteOpen, searchQuery]);

  const isAdmin = user?.role === 'ADMIN';
  const isDeveloper = user?.role === 'DEVELOPER';

  if (!isAdmin && !isDeveloper) {
    return (
      <ClientPortalShell
        user={user}
        logout={logout}
        navigate={navigate}
        toggleTheme={toggleTheme}
        location={location}
        profileOpen={profileOpen}
        setProfileOpen={setProfileOpen}
      />
    );
  }

  const navItemClass = ({ isActive }) => 
    `app-sidebar-item px-md py-2.5 mx-2 my-0.5 rounded-xl flex items-center gap-3 transition-all ${
      isActive 
        ? 'app-sidebar-item-active bg-primary/10 text-primary font-bold shadow-sm' 
        : 'text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface'
    }`;

  return (
    <div className="app-shell flex flex-col transition-colors duration-300 overflow-x-hidden">
      {/* Top Navigation Bar */}
      <header className="app-topbar flex justify-between items-center gap-sm px-gutter w-full h-12 sticky top-0 z-50 border-b border-outline-variant/60 bg-surface/80 backdrop-blur-md shadow-sm shadow-inverse-surface/5">
        <div className="flex items-center gap-md min-w-0">
          <Link to="/" className="flex items-center">
            <img src="/logo.png" alt="Company logo" className="h-9 w-auto object-contain" />
          </Link>
          <div className="hidden md:flex items-center ml-lg gap-lg">
            <nav className="flex gap-md h-12">
              <NavLink to="/dashboard" className={({ isActive }) => `flex items-center px-sm text-body-md transition-all ${isActive ? 'text-primary font-bold border-b-2 border-primary' : 'text-on-surface-variant hover:text-on-surface'}`}>
                Dashboard
              </NavLink>
              <NavLink to="/tickets" className={({ isActive }) => `flex items-center px-sm text-body-md transition-all ${isActive ? 'text-primary font-bold border-b-2 border-primary' : 'text-on-surface-variant hover:text-on-surface'}`}>
                Tickets
              </NavLink>
              <NavLink to="/kb" className={({ isActive }) => `flex items-center px-sm text-body-md ${isActive ? 'text-primary font-bold border-b-2 border-primary' : 'text-on-surface-variant hover:text-on-surface'}`}>
                Knowledge Base
              </NavLink>
            </nav>
          </div>
        </div>

        <div className="flex items-center gap-xs sm:gap-sm min-w-0">
          {/* Global Search Bar */}
          <form onSubmit={handleSearchSubmit} className="relative hidden sm:flex items-center bg-surface-muted border border-app rounded-lg px-sm py-1 mr-sm lg:mr-md group focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
            <span className="material-symbols-outlined text-on-surface-variant text-[18px]">search</span>
            <input 
              ref={searchInputRef}
              type="text" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none focus:ring-0 text-body-sm text-on-surface w-32 lg:w-48 placeholder:text-muted focus:w-40 lg:focus:w-64 transition-all duration-300 min-w-0" 
              placeholder="Search tickets... (Ctrl+K)"
              onFocus={() => setPaletteOpen(true)}
            />
            <span className="font-mono-data text-mono-data text-on-surface-variant/40 ml-2">⌘K</span>
          </form>

          {/* Notifications Trigger */}
          <button 
            onClick={() => navigate('/notifications')} 
            className="app-button-ghost p-2 rounded-full"
            title="Notifications"
          >
            <span className="material-symbols-outlined">notifications</span>
          </button>

          {/* Theme Toggle */}
          <button 
            onClick={toggleTheme} 
            className="app-button-ghost p-2 rounded-full"
            title="Toggle Theme"
          >
            <span className="material-symbols-outlined">settings_brightness</span>
          </button>

          {/* Profile Dropdown */}
          <div className="relative">
            <button 
              onClick={() => setProfileOpen(!profileOpen)}
              className="relative z-20 app-button-ghost flex items-center gap-2 cursor-pointer p-1 rounded-lg"
              title="User Account"
            >
              {user?.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-7 h-7 rounded-full object-cover border border-outline-variant" />
              ) : (
                <span className="material-symbols-outlined text-[28px]">account_circle</span>
              )}
            </button>

            {profileOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setProfileOpen(false)} />
                <div className="app-dropdown absolute right-0 mt-2 w-48 py-1 z-20 animate-in fade-in slide-in-from-top-1 duration-150">
                  <div className="px-4 py-2 border-b border-outline-variant/30">
                    <p className="text-body-sm font-semibold text-on-surface truncate">{user?.name}</p>
                    <p className="text-[11px] text-on-surface-variant truncate">{user?.email}</p>
                    <span className="inline-block mt-1 text-[9px] font-bold px-1.5 py-0.5 bg-primary-container text-on-primary-container rounded-full">
                      {user?.role}
                    </span>
                  </div>
                  <Link to="/settings" className="block px-4 py-2 text-body-sm text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface" onClick={() => setProfileOpen(false)}>
                    Profile Settings
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" className="block px-4 py-2 text-body-sm text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface" onClick={() => setProfileOpen(false)}>
                      Admin Panel
                    </Link>
                  )}
                  <button 
                    onClick={async () => { setProfileOpen(false); await logout(); navigate('/login'); }} 
                    className="w-full text-left block px-4 py-2 text-body-sm text-error hover:bg-error-container/20"
                  >
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="flex flex-1 relative">
        {/* Side Navigation Bar (Desktop) */}
        <aside className="app-sidebar hidden md:flex flex-col w-60 h-[calc(100vh-48px)] fixed left-0 top-12 border-r border-outline-variant/60 bg-surface/75 backdrop-blur-sm overflow-y-auto pt-4 pb-8">
          <nav className="flex flex-col gap-1 px-2">
              {isAdmin && (
                <>
                  <p className="px-md py-2 font-label-caps text-label-caps text-on-surface-variant/60 uppercase">Admin Console</p>
                <NavLink to="/admin" className={navItemClass}>
                  <span className="material-symbols-outlined">analytics</span>
                  <span className="font-body-md text-body-md">Dashboard</span>
                </NavLink>
                <NavLink to="/users" className={navItemClass}>
                  <span className="material-symbols-outlined">manage_accounts</span>
                  <span className="font-body-md text-body-md">Users</span>
                </NavLink>
                <NavLink to="/developers" className={navItemClass}>
                  <span className="material-symbols-outlined">engineering</span>
                  <span className="font-body-md text-body-md">Developers</span>
                </NavLink>
                <NavLink to="/admin/designations" className={navItemClass}>
                  <span className="material-symbols-outlined">work</span>
                  <span className="font-body-md text-body-md">Designations</span>
                </NavLink>
                <NavLink to="/tickets" end className={navItemClass}>
                  <span className="material-symbols-outlined">confirmation_number</span>
                  <span className="font-body-md text-body-md">Tickets</span>
                </NavLink>
                <NavLink to="/admin/services" className={navItemClass}>
                  <span className="material-symbols-outlined">design_services</span>
                  <span className="font-body-md text-body-md">Services</span>
                </NavLink>
                <NavLink to="/admin/service-requests" className={navItemClass}>
                  <span className="material-symbols-outlined">handshake</span>
                  <span className="font-body-md text-body-md">Service Requests</span>
                </NavLink>
                <NavLink to="/admin/fund-requests" className={navItemClass}>
                  <span className="material-symbols-outlined">request_quote</span>
                  <span className="font-body-md text-body-md">Fund Requests</span>
                </NavLink>
                <NavLink to="/admin/ledger" className={navItemClass}>
                  <span className="material-symbols-outlined">account_balance_wallet</span>
                  <span className="font-body-md text-body-md">Ledger</span>
                </NavLink>
                <NavLink to="/admin/invoices" className={navItemClass}>
                  <span className="material-symbols-outlined">payments</span>
                  <span className="font-body-md text-body-md">Invoices</span>
                </NavLink>
                <NavLink to="/admin/kyc" className={navItemClass}>
                  <span className="material-symbols-outlined">verified_user</span>
                  <span className="font-body-md text-body-md">KYC</span>
                </NavLink>
                <NavLink to="/kb" className={navItemClass}>
                  <span className="material-symbols-outlined">book</span>
                  <span className="font-body-md text-body-md">Knowledge Base</span>
                </NavLink>
                <NavLink to="/settings" className={navItemClass}>
                  <span className="material-symbols-outlined">settings</span>
                  <span className="font-body-md text-body-md">Settings</span>
                </NavLink>
              </>
            )}

            {isDeveloper && (
              <>
                <p className="px-md py-2 font-label-caps text-label-caps text-on-surface-variant/60 uppercase">Developer Portal</p>
                <NavLink to="/dashboard" className={navItemClass}>
                  <span className="material-symbols-outlined">dashboard</span>
                  <span className="font-body-md text-body-md">Dashboard</span>
                </NavLink>
                <NavLink to="/tickets" className={navItemClass}>
                  <span className="material-symbols-outlined">confirmation_number</span>
                  <span className="font-body-md text-body-md">Assigned Tickets</span>
                </NavLink>
                <NavLink to="/kb" className={navItemClass}>
                  <span className="material-symbols-outlined">book</span>
                  <span className="font-body-md text-body-md">Knowledge Base</span>
                </NavLink>
                <NavLink to="/notifications" className={navItemClass}>
                  <span className="material-symbols-outlined">notifications</span>
                  <span className="font-body-md text-body-md">Notifications</span>
                </NavLink>
                <NavLink to="/settings" className={navItemClass}>
                  <span className="material-symbols-outlined">settings</span>
                  <span className="font-body-md text-body-md">Profile</span>
                </NavLink>
              </>
            )}

            {!isAdmin && !isDeveloper && (
              <>
                <p className="px-md py-2 font-label-caps text-label-caps text-on-surface-variant/60 uppercase">Customer Panel</p>
                <NavLink to="/dashboard" className={navItemClass}>
                  <span className="material-symbols-outlined">dashboard</span>
                  <span className="font-body-md text-body-md">Dashboard</span>
                </NavLink>
                <NavLink to="/tickets" end className={navItemClass}>
                  <span className="material-symbols-outlined">confirmation_number</span>
                  <span className="font-body-md text-body-md">Tickets</span>
                </NavLink>
                <NavLink to="/tickets/new" className={navItemClass}>
                  <span className="material-symbols-outlined">add_box</span>
                  <span className="font-body-md text-body-md">Create Ticket</span>
                </NavLink>
                <NavLink to="/services" className={navItemClass}>
                  <span className="material-symbols-outlined">category</span>
                  <span className="font-body-md text-body-md">Services / Add-ons</span>
                </NavLink>
                <NavLink to="/fund-requests" className={navItemClass}>
                  <span className="material-symbols-outlined">request_quote</span>
                  <span className="font-body-md text-body-md">Fund Requests</span>
                </NavLink>
                <NavLink to="/ledger" className={navItemClass}>
                  <span className="material-symbols-outlined">account_balance_wallet</span>
                  <span className="font-body-md text-body-md">Ledger</span>
                </NavLink>
                <NavLink to="/invoices" className={navItemClass}>
                  <span className="material-symbols-outlined">receipt_long</span>
                  <span className="font-body-md text-body-md">Invoices</span>
                </NavLink>
                <NavLink to="/kyc" className={navItemClass}>
                  <span className="material-symbols-outlined">verified_user</span>
                  <span className="font-body-md text-body-md">KYC</span>
                </NavLink>
                <NavLink to="/kb" className={navItemClass}>
                  <span className="material-symbols-outlined">book</span>
                  <span className="font-body-md text-body-md">Knowledge Base</span>
                </NavLink>
                <NavLink to="/settings" className={navItemClass}>
                  <span className="material-symbols-outlined">settings</span>
                  <span className="font-body-md text-body-md">Settings</span>
                </NavLink>
              </>
            )}
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="ml-0 md:ml-60 flex-1 min-w-0 p-gutter sm:p-margin min-h-[calc(100vh-48px)] pb-24 md:pb-margin bg-background">
          <RenewalBanner />
          <Outlet />
        </main>
      </div>

      {paletteOpen && (
        <div className="fixed inset-0 z-[80] bg-scrim/30 flex items-start justify-center pt-20 px-md" onClick={() => setPaletteOpen(false)}>
          <form onSubmit={handleSearchSubmit} className="app-modal w-full max-w-xl overflow-hidden" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center gap-sm px-md py-sm border-b border-outline-variant">
              <span className="material-symbols-outlined text-on-surface-variant">search</span>
              <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="flex-1 bg-transparent outline-none text-on-surface"
                placeholder="Search tickets, users, developers, and KB"
                aria-label="Command palette search"
              />
              <button type="button" onClick={() => setPaletteOpen(false)} className="p-xs rounded hover:bg-surface-container">
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto p-sm">
              {paletteResults.length === 0 ? (
                <p className="px-md py-lg text-body-sm text-on-surface-variant">Type to search role-aware actions.</p>
              ) : paletteResults.map((item) => (
                <button
                  key={`${item.type}-${item.path}-${item.label}`}
                  type="button"
                  onClick={() => { setPaletteOpen(false); navigate(item.path); }}
                  className="w-full flex items-center justify-between px-md py-sm rounded hover:bg-surface-container text-left"
                >
                  <span className="text-body-md text-on-surface">{item.label}</span>
                  <span className="text-label-caps font-label-caps text-on-surface-variant uppercase">{item.type}</span>
                </button>
              ))}
            </div>
          </form>
        </div>
      )}

      {/* Mobile Navigation Bar (Bottom) */}
      <nav className="app-topbar md:hidden fixed bottom-0 left-0 right-0 h-16 border-t flex items-center justify-around z-50 transition-colors duration-300 shadow-lg shadow-inverse-surface/10">
        <NavLink to="/dashboard" className={({ isActive }) => `flex flex-col items-center gap-1 rounded-xl px-2 py-1 ${isActive ? 'bg-primary-container text-on-primary-container' : 'text-on-surface-secondary'}`}>
          <span className="material-symbols-outlined">dashboard</span>
          <span className="text-[10px] font-bold uppercase tracking-wider">Home</span>
        </NavLink>
        <NavLink to="/tickets" className={({ isActive }) => `flex flex-col items-center gap-1 rounded-xl px-2 py-1 ${isActive ? 'bg-primary-container text-on-primary-container' : 'text-on-surface-secondary'}`}>
          <span className="material-symbols-outlined">confirmation_number</span>
          <span className="text-[10px] font-bold uppercase tracking-wider">Tickets</span>
        </NavLink>
        {!isAdmin && !isDeveloper && (
          <button 
            onClick={() => navigate('/tickets/new')}
            className="bg-primary text-on-primary w-12 h-12 rounded-full flex items-center justify-center -translate-y-4 shadow-lg shadow-primary/30 active:scale-95 transition-all"
          >
            <span className="material-symbols-outlined">add</span>
          </button>
        )}
        <NavLink to="/kb" className={({ isActive }) => `flex flex-col items-center gap-1 rounded-xl px-2 py-1 ${isActive ? 'bg-primary-container text-on-primary-container' : 'text-on-surface-secondary'}`}>
          <span className="material-symbols-outlined">book</span>
          <span className="text-[10px] font-bold uppercase tracking-wider">Docs</span>
        </NavLink>
        <NavLink to="/settings" className={({ isActive }) => `flex flex-col items-center gap-1 rounded-xl px-2 py-1 ${isActive ? 'bg-primary-container text-on-primary-container' : 'text-on-surface-secondary'}`}>
          <span className="material-symbols-outlined">settings</span>
          <span className="text-[10px] font-bold uppercase tracking-wider">Settings</span>
        </NavLink>
      </nav>
    </div>
  );
}
